import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaBell, FaEraser, FaEye, FaFileExport, FaMapMarkedAlt, FaPaperclip, FaPen, FaPlus, FaSearch} from "react-icons/fa";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {normalizeFilterText} from "../../../../utils/normalizeFilterText.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import useFetchData from "./hooks/use-fetch-data.js";
import ContactIdService from "../../../../services/web/contact-id-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import AlarmArmDisarmModal from "./components/modal-alarm.jsx";
import {PermissionEnum} from "../../../../utils/enums/permission-enum.js";
import useHasPermission from "../../../../hooks/use-has-permission.js";
import ModalAttachesList from "./components/modal-attaches.jsx";
import ReportUtils from "../../../../utils/report-utils.js";
import AgGridUtils from "../../../../utils/aggrid-utils.js";

const API_URL = import.meta.env.VITE_API_URL;
const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const initialFilter = {
    status: "",
    account: "",
    type: "",
    lastEvent: "",
    description: "",
    fireAlarm: null
};

const ContactIdList = () => {
    const navigate = navigateHook();
    const hasPermission = useHasPermission();
    const fetchData = useFetchData();

    const [filters, setFilters] = useState(initialFilter);
    const [appliedFilters, setAppliedFilters] = useState(initialFilter);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [alarmModal, setAlarmModal] = useState({
        open: false,
        mainAccount: null,
        description: ""
    });

    const [alarmStatus, setAlarmStatus] = useState(null);
    const [loadingAlarm, setLoadingAlarm] = useState(false);
    const [attachModal, setAttachModal] = useState({
        open: false,
        attaches: null,
    });

    const gridRef = useRef(null);
    const cacheRef = useRef([]);

    const newPlace = () => {
        const urlDev = "/contact-id/form";
        navigate(urlDev, true);
    };

    const openPlace = (id) => {
        const urlDev = `/contact-id/form/${id}`;
        navigate(urlDev, true);
    };

    const openEvents = (id, alarmSystemId, fireAlarm) => {
        const mode = fireAlarm ? "fire-alarm" : "event";

        const urlDev = `/contact-id-events/${mode}/${id}?alarmSystemId=${alarmSystemId}`;
        
        navigate(urlDev, true);
    };

    const calculateDatesLastStatus = date => {
        if (!date) return "";

        const diff = Date.now() - new Date(date).getTime();
        let sec = Math.floor(diff / 1000);

        sec = Math.max(sec, 1);

        if (sec >= 86400) return `${Math.floor(sec / 86400)}d`;
        if (sec >= 3600) return `${Math.floor(sec / 3600)}h`;
        if (sec >= 60) return `${Math.floor(sec / 60)}min`;
        return `${sec}seg`;
    };

    const normalizeItem = useCallback(item => {
        const timeAgo = item.time ? calculateDatesLastStatus(item.time) : "";
        const lastMsg = item.lastEvent?.data?.message ?? "";

        return {
            ...item,
            descriptionFormat:
                item.place && item.description
                    ? `${item.place} - ${item.description}`
                    : item.place || item.description || "",
            timeFormat:
                timeAgo || lastMsg
                    ? `${timeAgo ? `Há ${timeAgo} - ` : ""}${lastMsg}`
                    : ""
        };
    }, []);

    useEffect(() => {
        const source = new EventSource(`${API_KEEPER}/contactid/sse`);

        source.onmessage = e => {
            if (!e.data || e.data === "[]" || e.data === "heartbeat") return;

            const payload = JSON.parse(e.data);

            if (["openDVR", "testDVR", "kill_session"].includes(payload?.type)) {
                return;
            }

            let cache = [...cacheRef.current];
            let accounts;

            if (Array.isArray(payload)) {
                accounts = payload.map(normalizeItem);
            } else {
                let match = false;

                const normalized = normalizeItem(payload);
                const index = cache.findIndex(item => item.id === payload.id);

                if (payload.type === "remove" || payload.removed === true) {
                    cache = cache.filter(item => item.id !== payload.id);
                } else if (index !== -1) {
                    cache[index] = normalized;
                    match = true;
                } else {
                    cache.push(normalized);
                    match = true;
                }

                accounts = cache;
            }

            accounts = accounts.sort((a, b) => {
                const aS = a.primaryEvent ? 1 : a.warningEvents ? 2 : 3;
                const bS = b.primaryEvent ? 1 : b.warningEvents ? 2 : 3;
                return aS - bS;
            });

            cacheRef.current = accounts;
            setData(accounts);
        };

        source.onerror = () => source.close();
        return () => source.close();
    }, [normalizeItem]);

    const filterItems = useCallback((items, currentFilters) => (
        items.filter(item =>
            Object.entries(currentFilters).every(([key, filter]) => {
                if (filter === "" || filter === null || filter === undefined) {
                    return true;
                }

                if (key === "description") {
                    return (
                        normalizeFilterText(item.description)
                            .includes(normalizeFilterText(filter)) ||
                        normalizeFilterText(item.place)
                            .includes(normalizeFilterText(filter))
                    );
                }

                if (key === "lastEvent") {
                    const lastEventMessage = item.lastEvent?.message ?? item.lastEvent?.data?.message;

                    return normalizeFilterText(lastEventMessage)
                        .includes(normalizeFilterText(filter?.label));
                }

                if (key === "type") {
                    return normalizeFilterText(item.type ?? "")
                        .includes(normalizeFilterText(filter?.value));
                }

                if (key === "fireAlarm") {
                    return item.fireAlarm === filter?.value;
                }

                return normalizeFilterText(item[key] ?? "")
                    .includes(normalizeFilterText(filter));
            })
        )
    ), []);

    const handleSearch = useCallback(() => {
        setAppliedFilters(filters);
        setFilteredData(filterItems(data, filters));
    }, [data, filterItems, filters]);

    const clearFilter = useCallback(() => {
        setFilters(initialFilter);
        setAppliedFilters(initialFilter);
        setFilteredData(data);
    }, [data]);

    useEffect(() => {
        const handler = e => {
            if (e.key === "Enter") handleSearch();
            if (e.key === "Escape") clearFilter();
        };
        window.addEventListener("keyup", handler);
        return () => window.removeEventListener("keyup", handler);
    }, [clearFilter, handleSearch]);

    useEffect(() => {
        setFilteredData(filterItems(data, appliedFilters));
    }, [appliedFilters, data, filterItems]);

    useEffect(() => {
        const updated = cacheRef.current.map(item => normalizeItem(item));
        setData(updated);
    }, [normalizeItem]);

    const footerStats = useMemo(() => {
        let alarmConnected = 0;
        let alarmDisconnected = 0;
        let alarmOnline = 0;
        let alarmOffline = 0;

        filteredData.forEach(c => {
            if (!c.fireAlarm) {
                if (c.status === "Armado") alarmConnected++;
                if (c.status === "Desarmado") alarmDisconnected++;
            } else {
                if (c.status === "Online") alarmOnline++;
                if (c.status === "Offline") alarmOffline++;
            }
        });

        return {
            total: filteredData.length,
            alarmConnected,
            alarmDisconnected,
            alarmOnline,
            alarmOffline
        };
    }, [filteredData]);

    const openModalArm = async (mainAccount,description) => {
        setIsLoading(true);
        await loadStatus(mainAccount, description);
    }

    const loadStatus = async (mainAccount, description) => {
        try {
            setLoadingAlarm(true);

            const { data } = await ContactIdService.alarmStatus(mainAccount);

            if (data?.error && data?.message) {
                projectUtils.swalErrorMessage("", data.message, "Ok");
                return;
            }

            setAlarmStatus(data);
            setAlarmModal({ open: true, mainAccount, description });
        } catch (error) {
            console.error(error);
            projectUtils.swalErrorMessage("Erro", "Não foi possível carregar informações."
            );
        } finally {
            setLoadingAlarm(false);
            setIsLoading(false);
        }
    };

    const armDisarm = async (func, partition, account = alarmModal.mainAccount) => {
        try {
            setLoadingAlarm(true);

            await ContactIdService.armingDisarming(
                func,
                partition,
                account,
                alarmModal.mainAccount
            );

            await loadStatus(alarmModal.mainAccount, alarmModal.description);
        } catch (error) {
            console.error(error);

            let message = "Não foi possível prosseguir.";
            if (error?.response?.status !== 500) {
                message = error.response?.data;
            }

            projectUtils.swalErrorMessage("", message, "Ok");
        } finally {
            setLoadingAlarm(false);
        }
    };

    const columnDefs = useMemo(
        () => [
            { headerName: "Status", field: "status", hozAlign: "center", width: 110 },
            { headerName: "Tipo",field: "type", width: 120,
                valueFormatter: ({ value }) => {
                    if (!value) return "";
                    const v = value.toLowerCase();
                    if (v === "comercial" || v === "COMERCIO") return "Comercial";
                    if (v === "school") return "Escola";
                    return value;
                },
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            { headerName: "Descrição", field: "descriptionFormat", flex: 1, comparator: AgGridUtils.nullLastComparator },
            { headerName: "Conta", field: "account",
                valueFormatter: ({ data, value }) =>
                    data?.details ? `${value} - ${data.details}` : value
            },
            { headerName: "Último evento", field: "timeFormat", width: 220,
                comparator: (_timeFormatA, _timeFormatB, nodeA, nodeB, isInverted) => {
                    const timeA = nodeA.data?.time ? new Date(nodeA.data.time).getTime() : null;
                    const timeB = nodeB.data?.time ? new Date(nodeB.data.time).getTime() : null;

                    return aggridUtils.nullLastComparator(timeA, timeB, nodeA, nodeB, isInverted);
                }
            },
            { headerName: "Eventos", width: 100, sort: "desc",
                comparator: (_a, _b, nodeA, nodeB) => {
                    const a = nodeA.data;
                    const b = nodeB.data;

                    const aS = a.primaryEvent ? 3 : a.warningEvents ? 2 : 1;
                    const bS = b.primaryEvent ? 3 : b.warningEvents ? 2 : 1;

                    return aS - bS;
                },
                cellRenderer: ({data}) => {
                    if (data?.removed) return null;

                    let icon = "sentry.event.alarm.svg";
                    if (data.primaryEvent) icon = "sentry.event.alarm-danger.svg";
                    else if (data.warningEvents) icon = "sentry.event.alarm-alert.svg";

                    return (
                        <span className="cursor-pointer flex justify-center w-full" onClick={() => openEvents(data.id, data.alarmSystemId, data.fireAlarm)}>
                            <img src={`${API_URL}/public/assets/img/contactid/${icon}`} className={"w-[32px]"} alt="evento" title={"Abrir eventos"} />
                        </span>
                    );
                }
            },
            { headerName: "Ações", width: 230, sortable: false,
                cellRenderer: ({ data }) =>
                    data?.removed ? null : (
                        <div className="flex items-center justify-center gap-1 h-full">
                            <Button className={"!px-3"} title={"Armar/desarmar"} disabled={data.driver?.toUpperCase() !== "INTELBRAS"} onClick={() => openModalArm(data.mainAccount, data.descriptionFormat)}>
                                <FaBell/>
                            </Button>

                            <Button className={"!px-3"} title={"Abrir mapa em tempo real"} onClick={() => navigate(`/gis?item_id=contactid-${data.id}`, true)}>
                                <FaMapMarkedAlt/>
                            </Button>

                            <Button className={"!px-3"} title={"Abrir DVR"} disabled={!data.dvrEnable} onClick={() => projectUtils.openDvr(data.id)}>
                                DVR
                            </Button>

                            <Button
                                className={"!px-3"}
                                title={"Visualizar anexos"}
                                disabled={!data.attaches || !data.attaches.length}
                                onClick={() => setAttachModal({open: true, attaches: data.attaches})}
                            >
                                <FaPaperclip/>
                            </Button>

                            <Button className={"!px-3"} title={"Editar"} onClick={() => openPlace(data.alarm_system_id)}>
                                { data.permissionEdit ? (<FaPen/>) : (<FaEye/>) }
                            </Button>
                        </div>
                    )
            }
        ],
        []
    );

    const theme = useMemo(() => aggridUtils.generalTheme(40, 40), []);

    const closeAlarmModal = () => {
        setAlarmModal({
            open: false,
            mainAccount: null,
            description: ""
        });
        setAlarmStatus(null);
    };

    const closeAttachModal = () => {
        setAttachModal({
            open: false,
            attaches: []
        });
    };

    useEffect(() => {

        const values = [];
        const labels = ['Status', 'Conta', 'Descrição', 'Local pré-cadastrado', 'Tipo', 'Logradouro', 'Número', 'Bairro', 'Complemento', 'Município', 'Último evento'];

        const formatEvent = (event, time) => {
            if (!event) return "";

            const timeAgo = time ? calculateDatesLastStatus(time) : "";
            const lastMsg = event.data?.message ?? "";

            return `${timeAgo || lastMsg ? `${timeAgo ? `Há ${timeAgo} - ` : ""}${lastMsg}` : ""}`;
        };

        filteredData.forEach((element) => {
            values.push([
                element.status,
                `${element.account ?? element.mainAccount} ${element.details ? ` - ${element.details}` : ""}`,
                element.description,
                element.place,
                element.type,
                element.address,
                element.number,
                element.neighborhood,
                element.complement,
                element.city,
                formatEvent(element.lastEvent, element.time)
            ]);
        });

        if (values.length !== 0) ReportUtils.createExportableTable(labels, values, 'Monitoramento Patrimonial');

    }, [filteredData]);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Monitoramento Patrimonial</Template.Title>
                    <div className={"flex flex-row gap-2"}>
                        <Button target='success' onClick={() => ReportUtils.export({ exportInSheets: true, customFileName: 'monitoramento' })}>
                            <FaFileExport/> Exportar
                        </Button>

                        { hasPermission(PermissionEnum.CONTACTID_ADMIN) &&
                            <Button onClick={newPlace}><FaPlus/> Adicionar local</Button>
                        }
                    </div>
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                            <InputForm
                                id={"status"}
                                label="Status"
                                value={filters.status}
                                classNameWrapper="col-span-3"
                                onChange={e => setFilters(p => ({...p, status: e.target.value}))}
                            />

                            <InputForm
                                id={"account"}
                                label="Conta"
                                value={filters.account}
                                classNameWrapper="col-span-3"
                                onChange={e => setFilters(p => ({...p, account: e.target.value}))}
                            />

                            <SelectComponent
                                label="Tipo"
                                options={fetchData.locationTypes}
                                value={filters.type}
                                onChange={v => setFilters(p => ({ ...p, type: v }))}
                                classNameWrapper="col-span-3"
                            />

                            <SelectComponent
                                label="Último evento"
                                options={fetchData.lastEvent}
                                value={filters.lastEvent}
                                classNameWrapper="col-span-3"
                                onChange={v => setFilters(p => ({...p, lastEvent: v}))}
                            />

                            <SelectComponent
                                label="Tipo de central"
                                value={filters.fireAlarm}
                                classNameWrapper="col-span-3"
                                options={[
                                    { value: false, label: "Central de alarme" },
                                    { value: true, label: "Central de incêndio" }
                                ]}
                                onChange={v => setFilters(p => ({ ...p, fireAlarm: v }))}
                            />

                            <div className="col-span-9 flex gap-4 items-end">
                                <InputForm
                                    id={"description"}
                                    label="Descrição"
                                    value={filters.description}
                                    classNameWrapper="flex-1"
                                    onChange={e =>
                                        setFilters(p => ({
                                            ...p,
                                            description: e.target.value
                                        }))
                                    }
                                />

                                <div className={"flex gap-2"}>
                                    <Button target="primary-outline" title={"Limpar"} onClick={clearFilter}>
                                        <FaEraser />
                                    </Button>

                                    <Button target="primary" title={"Buscar"} onClick={handleSearch}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2 h-[calc(100vh-175px)]">
                    <Card.Body className="flex flex-col gap-2 h-full">
                        <div className={"h-full table-contact-id"}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={filteredData}
                                columnDefs={columnDefs}
                                getRowId={params => params.data.id}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                rowHeight={40}
                                theme={theme}
                            />
                        </div>

                        <div className="flex justify-center items-center gap-4 pt-0.5">
                            <div className={"flex justify-center items-center gap-1"}>
                                <span>Total de centrais:</span>
                                <span className={"bg-white border border-gray-300 px-3 rounded"}>{footerStats.total}</span>
                            </div>

                            <div className={"flex justify-center items-center gap-1"}>
                                <span>Central de alarme armada:</span>
                                <span className={"bg-green-700 text-white px-3 rounded"}>{footerStats.alarmConnected}</span>
                            </div>

                            <div className={"flex justify-center items-center gap-1"}>
                                <span>Central de alarme desarmada:</span>
                                <span className={"bg-red-700 text-white px-3 rounded"}>{footerStats.alarmDisconnected}</span>
                            </div>

                            <div className={"flex justify-center items-center gap-1"}>
                                <span>Central de incêndio online:</span>
                                <span className={"bg-green-700 text-white px-3 rounded"}>{footerStats.alarmOnline}</span>
                            </div>

                            <div className={"flex justify-center items-center gap-1"}>
                                <span>Central de incêndio offline:</span>
                                <span className={"bg-red-700 text-white px-3 rounded"}>{footerStats.alarmOffline}</span>
                            </div>
                        </div>

                        <div id="chart-tables"/>
                    </Card.Body>
                </Card>
            </Template.Body>

            <AlarmArmDisarmModal
                open={alarmModal.open}
                setOpen={closeAlarmModal}
                description={alarmModal.description}
                alarmStatus={alarmStatus}
                loading={loadingAlarm}
                onArmDisarm={armDisarm}
            />

            <ModalAttachesList
                open={attachModal.open}
                setOpen={closeAttachModal}
                attaches={attachModal.attaches}
            />
        </Template>
    );
};

export default ContactIdList;
